import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DocumentationCategory,
  useEdmDocumentationCategoryCreateMutation,
  useEdmDocumentationCategoryDestroyMutation,
  useEdmDocumentationCategoryQuery,
  useEdmDocumentationCategoryUpdateMutation,
} from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import onDestroyListItemCallBack from '@lib/utils/onDestroyListItemCallBack';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import useSearchParam from '@lib/hooks/useSearchParam';
import { Values } from '@lib/interfaces/form';
import useEdmDocumentationCategoriesQueryHook from 'utils/fetch/hooks/useEdmDocumentationCategoriesQueryHook';
import getPreparedDataFromValues from './getPreparedDataFromValues';

interface UseActionsProps {
  closeCategoryModal: () => void;
  closeDeleteModal: () => void;
  isPlanLocked: boolean;
  openCategoryModal: () => void;
  openDeleteModal: () => void;
  selectedCategory: DocumentationCategory | null;
  setSelectedCategory: (v: DocumentationCategory) => void;
}

export default function useActions(props: UseActionsProps) {
  const {
    closeCategoryModal,
    closeDeleteModal,
    isPlanLocked,
    openCategoryModal,
    openDeleteModal,
    selectedCategory,
    setSelectedCategory,
  } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const [searchQuery] = useSearchParam();
  const [paginationParams] = usePaginationParams();

  const useEdmDocumentationCategoriesQueryHookResult =
    useEdmDocumentationCategoriesQueryHook({
      searchQuery,
      paginationParams,
      skip: isPlanLocked,
    });

  const categoryQuery = useEdmDocumentationCategoryQuery({
    skip: selectedCategory === null,
    variables: {
      id: selectedCategory?.id || '',
    },
  });

  const [categoryCreateMutation, categoryCreateMutationResult] =
    useEdmDocumentationCategoryCreateMutation({
      onCompleted: () => {
        useEdmDocumentationCategoriesQueryHookResult.refetch();
        if (closeCategoryModal) closeCategoryModal();
        toast({ content: t('create-category-success') });
      },
      onError: (error) => showGraphQLErrors(error, t),
    });

  const [categoryUpdateMutation, categoryUpdateMutationResult] =
    useEdmDocumentationCategoryUpdateMutation({
      onCompleted: () => {
        useEdmDocumentationCategoriesQueryHookResult.refetch();
        if (closeCategoryModal) closeCategoryModal();
        toast({ content: t('update-category-success') });
      },
      onError: (error) => showGraphQLErrors(error, t),
    });

  const [destroyMutation] = useEdmDocumentationCategoryDestroyMutation({
    onCompleted: () => {
      closeDeleteModal();
      toast({ content: t('destroy-category-success') });
      onDestroyListItemCallBack({
        collection: useEdmDocumentationCategoriesQueryHookResult.collection,
        navigate,
        pathname,
        refetch: useEdmDocumentationCategoriesQueryHookResult.refetch,
        search,
        totalPages: useEdmDocumentationCategoriesQueryHookResult.totalPages,
      });
    },
    onError: (error) => showGraphQLErrors(error, t),
  });

  const onEditHandler = (v?: DocumentationCategory) => {
    if (v) {
      setSelectedCategory(v);
      openCategoryModal();
    }
  };

  const onDeleteHandler = (v: DocumentationCategory) => {
    setSelectedCategory(v);
    openDeleteModal();
  };

  const onConfirmedDelete = () => {
    if (selectedCategory) {
      destroyMutation({ variables: { id: selectedCategory.id } });
    }
  };

  const onCategoryCreate = (values: Values) => {
    const attributes = getPreparedDataFromValues(values);
    categoryCreateMutation({ variables: { attributes } });
  };

  const onCategoryUpdate = (values: Values) => {
    const attributes = getPreparedDataFromValues(values);
    categoryUpdateMutation({
      variables: {
        attributes,
        id: selectedCategory?.id || '',
      },
    });
  };

  const {
    error: categoryCreateMutationError,
    loading: categoryCreateMutationLoading,
    reset: categoryCreateMutationReset,
  } = categoryCreateMutationResult;
  const {
    error: categoryUpdateMutationError,
    loading: categoryUpdateMutationLoading,
    reset: categoryUpdateMutationReset,
  } = categoryUpdateMutationResult;

  const categoryMutationError = selectedCategory
    ? categoryUpdateMutationError
    : categoryCreateMutationError;
  const categoryMutationLoading = selectedCategory
    ? categoryUpdateMutationLoading
    : categoryCreateMutationLoading;
  const categoryMutationReset = useCallback(() => {
    categoryUpdateMutationReset();
    categoryCreateMutationReset();
  }, [categoryUpdateMutationReset, categoryCreateMutationReset]);
  const onSubmitCategoryForm = selectedCategory
    ? onCategoryUpdate
    : onCategoryCreate;

  return {
    categoryQuery,
    categoryMutationError,
    categoryMutationLoading,
    categoryMutationReset,
    onConfirmedDelete,
    onDeleteHandler,
    onEditHandler,
    onSubmitCategoryForm,
    useEdmDocumentationCategoriesQueryHookResult,
  };
}
